import { offsetMinutes, zonedToUtc, offsetLabel } from "./tz";

/**
 * Meeting planner maths for the Time Zone Converter (B-0117). Every clock time comes from the browser's own Intl / IANA data
 * through tz.ts (offsetMinutes, zonedToUtc): nothing here hard-codes a UTC offset or a daylight-saving rule, so a zone's
 * clock changes are whatever the device's time zone database says. The only tables in this file are labels (which country a
 * zone belongs to, a metro name for display) and never feed the arithmetic.
 */

const pad = (n: number) => String(n).padStart(2, "0");

// ---------- friendly names ----------

/** ISO country of the well-known zones, for "Mumbai, India". Labels only. Other zones show the city from their IANA name. */
const ZONE_REGION: Record<string, string> = {
  "Asia/Kolkata": "IN", "Asia/Kathmandu": "NP", "Asia/Dhaka": "BD", "Asia/Colombo": "LK", "Asia/Karachi": "PK", "Asia/Dubai": "AE", "Asia/Riyadh": "SA", "Asia/Tehran": "IR",
  "Asia/Jerusalem": "IL", "Asia/Singapore": "SG", "Asia/Hong_Kong": "HK", "Asia/Shanghai": "CN", "Asia/Tokyo": "JP", "Asia/Seoul": "KR", "Asia/Bangkok": "TH", "Asia/Jakarta": "ID",
  "Asia/Manila": "PH", "Asia/Kuala_Lumpur": "MY", "Asia/Ho_Chi_Minh": "VN", "Asia/Taipei": "TW", "Asia/Yangon": "MM", "Europe/London": "GB", "Europe/Dublin": "IE", "Europe/Paris": "FR",
  "Europe/Berlin": "DE", "Europe/Madrid": "ES", "Europe/Rome": "IT", "Europe/Amsterdam": "NL", "Europe/Zurich": "CH", "Europe/Moscow": "RU", "Europe/Istanbul": "TR", "Europe/Kyiv": "UA",
  "Europe/Lisbon": "PT", "Europe/Athens": "GR", "Europe/Stockholm": "SE", "Europe/Warsaw": "PL", "Europe/Vienna": "AT", "Europe/Brussels": "BE", "Europe/Prague": "CZ", "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI", "Europe/Oslo": "NO", "Europe/Budapest": "HU", "Europe/Bucharest": "RO",
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US", "America/Phoenix": "US", "America/Los_Angeles": "US", "America/Anchorage": "US", "Pacific/Honolulu": "US",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Halifax": "CA", "America/St_Johns": "CA", "America/Mexico_City": "MX", "America/Sao_Paulo": "BR", "America/Argentina/Buenos_Aires": "AR",
  "America/Bogota": "CO", "America/Lima": "PE", "America/Santiago": "CL", "America/Caracas": "VE", "America/Havana": "CU",
  "Africa/Johannesburg": "ZA", "Africa/Lagos": "NG", "Africa/Cairo": "EG", "Africa/Nairobi": "KE", "Africa/Casablanca": "MA", "Africa/Accra": "GH", "Africa/Addis_Ababa": "ET",
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU", "Australia/Perth": "AU", "Australia/Brisbane": "AU", "Australia/Adelaide": "AU", "Australia/Lord_Howe": "AU", "Pacific/Auckland": "NZ",
  "Pacific/Fiji": "FJ", "Pacific/Pago_Pago": "AS", "Pacific/Kiritimati": "KI",
};
/** Where the everyday name of the metro differs from the IANA city (India has one zone, so one label is unambiguous). */
const ZONE_CITY: Record<string, string> = { "Asia/Kolkata": "Mumbai", "Europe/Kyiv": "Kyiv" };

/** "America/Argentina/Buenos_Aires" -> "Buenos Aires"; "UTC" -> "UTC". */
export function zoneCity(id: string): string {
  if (ZONE_CITY[id]) return ZONE_CITY[id];
  if (id === "UTC") return "UTC";
  const last = id.split("/").pop() ?? id;
  return last.replace(/_/g, " ");
}

export function zoneCountry(id: string): string | undefined {
  const code = ZONE_REGION[id];
  if (!code) return undefined;
  try { return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? undefined; } catch { return undefined; }
}

const abbrCache = new Map<string, Intl.DateTimeFormat | null>();
function abbrFormat(loc: string, id: string): Intl.DateTimeFormat | null {
  const k = `${loc}|${id}`;
  if (!abbrCache.has(k)) {
    try { abbrCache.set(k, new Intl.DateTimeFormat(loc, { timeZone: id, timeZoneName: "short" })); } catch { abbrCache.set(k, null); }
  }
  return abbrCache.get(k)!;
}

/** The zone's own short name at that moment (EST, BST, IST, AEDT) when the device knows one; "" when it would only be "GMT+9". */
export function zoneAbbr(utcMs: number, id: string): string {
  const region = ZONE_REGION[id];
  const locales = region ? [`en-${region}`, "en-US"] : ["en-US"];
  for (const loc of locales) {
    const f = abbrFormat(loc, id);
    if (!f) continue;
    const name = f.formatToParts(new Date(utcMs)).find((p) => p.type === "timeZoneName")?.value ?? "";
    if (name && !/^(GMT|UTC)[+\-−]/.test(name)) return name;
  }
  return "";
}

export interface FriendlyZone { title: string; detail: string; iana: string; text: string }
/** "Mumbai, India" with "IST, UTC+5:30" beside it, and the IANA id kept as the secondary line. */
export function friendlyZone(utcMs: number, id: string): FriendlyZone {
  const country = zoneCountry(id);
  const title = country && country !== zoneCity(id) ? `${zoneCity(id)}, ${country}` : zoneCity(id);
  const abbr = zoneAbbr(utcMs, id);
  const off = offsetLabel(utcMs, id);
  const detail = abbr && abbr !== off ? `${abbr}, ${off}` : off;
  return { title, detail, iana: id, text: `${title} (${detail})` };
}

// ---------- next clock change ----------

export interface ClockChange { utc: number; fromMin: number; toMin: number; deltaMin: number }

/** The next time the zone's UTC offset changes after `fromUtc` within `horizonDays`, found to the minute; null when it does not change (India, UTC, Arizona). */
export function nextClockChange(id: string, fromUtc: number, horizonDays = 400): ClockChange | null {
  const DAY = 86400000, MIN = 60000;
  let lo = fromUtc, loOff = offsetMinutes(lo, id);
  for (let d = 1; d <= horizonDays; d++) {
    const hi = fromUtc + d * DAY;
    const hiOff = offsetMinutes(hi, id);
    if (hiOff !== loOff) {
      let a = lo, b = hi;
      while (b - a > MIN) { const mid = Math.floor((a + b) / 2 / MIN) * MIN; if (mid <= a) break; if (offsetMinutes(mid, id) === loOff) a = mid; else b = mid; }
      const after = offsetMinutes(b, id);
      return { utc: b, fromMin: loOff, toMin: after, deltaMin: after - loOff };
    }
    lo = hi; loOff = hiOff;
  }
  return null;
}

const fmtDelta = (m: number) => { const a = Math.abs(m); const h = Math.floor(a / 60), mm = a % 60; return `${h ? `${h} hour${h === 1 ? "" : "s"}` : ""}${h && mm ? " " : ""}${mm ? `${mm} minutes` : ""}`; };

/**
 * "Sun 1 Nov 2026, 02:00 local time: clocks go back 1 hour (UTC-4 to UTC-5), in 2 days". The time is the reading on the clock just
 * BEFORE the change, which is how people say it ("at 2:00 am"). `now` is passed in so tests are exact.
 */
export function clockChangeText(_id: string, c: ClockChange, now: number): string {
  const before = new Date(c.utc + c.fromMin * 60000);
  const day = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric", month: "short", year: "numeric" }).format(before).replace(/,/g, "");
  const clock = `${pad(before.getUTCHours())}:${pad(before.getUTCMinutes())}`;
  const days = Math.max(0, Math.floor((c.utc - now) / 86400000));
  const dir = c.deltaMin > 0 ? "forward" : "back";
  const label = (m: number) => `UTC${m < 0 ? "-" : "+"}${Math.floor(Math.abs(m) / 60)}${Math.abs(m) % 60 ? ":" + String(Math.abs(m) % 60).padStart(2, "0") : ""}`;
  return `${day}, ${clock} local time: clocks go ${dir} ${fmtDelta(c.deltaMin)} (${label(c.fromMin)} to ${label(c.toMin)}), ${days === 0 ? "within a day" : `in ${days} day${days === 1 ? "" : "s"}`}`;
}

// ---------- the hour grid ----------

export interface WorkHours { start: number; end: number }
export const DEFAULT_WORK: WorkHours = { start: 9, end: 17 };

export interface GridCell { local: string; date: string; dayShift: number; working: boolean; changed: boolean; minutes: number }
export interface GridColumn { utc: number; cells: GridCell[]; allWorking: boolean }
export interface MeetingGrid { zones: string[]; columns: GridColumn[]; windows: { from: number; to: number }[] }

function localParts(utc: number, zone: string) {
  const off = offsetMinutes(utc, zone);
  const d = new Date(utc + off * 60000);
  return { y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes(), off };
}

/**
 * One column per hour of the FIRST zone's calendar day (23, 24 or 25 columns on the day its clocks change), and for every zone the
 * local time at that exact instant. Working hours are checked on each zone's own clock. Columns where every zone is inside working
 * hours are merged into `windows` (indexes of the first and last column).
 */
export function buildGrid(zones: string[], day: { y: number; mo: number; d: number }, work: WorkHours = DEFAULT_WORK): MeetingGrid {
  const ref = zones[0];
  const start = zonedToUtc(day.y, day.mo, day.d, 0, 0, ref).utc;
  const nextDay = new Date(Date.UTC(day.y, day.mo - 1, day.d) + 86400000);
  const end = zonedToUtc(nextDay.getUTCFullYear(), nextDay.getUTCMonth() + 1, nextDay.getUTCDate(), 0, 0, ref).utc;
  const HOUR = 3600000;
  const columns: GridColumn[] = [];
  const refDate = `${day.y}-${pad(day.mo)}-${pad(day.d)}`;
  for (let u = start; u < end; u += HOUR) {
    const cells = zones.map((z) => {
      const p = localParts(u, z);
      const prev = offsetMinutes(u - HOUR, z);
      const minutes = p.h * 60 + p.mi;
      const date = `${p.y}-${pad(p.mo)}-${pad(p.d)}`;
      const shift = Math.round((Date.UTC(p.y, p.mo - 1, p.d) - Date.UTC(day.y, day.mo - 1, day.d)) / 86400000);
      return { local: `${pad(p.h)}:${pad(p.mi)}`, date, dayShift: date === refDate ? 0 : shift, working: minutes >= work.start * 60 && minutes < work.end * 60, changed: prev !== p.off, minutes } as GridCell;
    });
    columns.push({ utc: u, cells, allWorking: cells.every((c) => c.working) });
  }
  const windows: { from: number; to: number }[] = [];
  columns.forEach((c, i) => { if (c.allWorking) { const last = windows[windows.length - 1]; if (last && last.to === i - 1) last.to = i; else windows.push({ from: i, to: i }); } });
  return { zones, columns, windows };
}

/** "Everyone is in working hours from 14:00 to 17:00 in New York" or why not. */
export function overlapText(g: MeetingGrid, work: WorkHours = DEFAULT_WORK): string {
  const city = zoneCity(g.zones[0]);
  if (g.zones.length < 2) return "Add a second time zone to see when everyone is at work.";
  if (g.windows.length === 0) return `No hour on this day falls inside ${pad(work.start)}:00 to ${pad(work.end)}:00 in every zone. Widen the working hours or pick a slot that is early or late for someone.`;
  const parts = g.windows.map((w) => {
    const endH = Number(g.columns[w.to].cells[0].local.slice(0, 2)), endM = g.columns[w.to].cells[0].local.slice(3);
    return `from ${g.columns[w.from].cells[0].local} to ${pad((endH + 1) % 24)}:${endM}`;
  });
  return `Everyone is inside working hours (${pad(work.start)}:00 to ${pad(work.end)}:00 local) ${parts.join(" and ")} in ${city}.`;
}

// ---------- share link ----------

export interface ShareState { zones: string[]; time: string; work: WorkHours }
export const MAX_SHARE_ZONES = 7;

function zoneOk(z: string): boolean {
  try { new Intl.DateTimeFormat("en-US", { timeZone: z }); return true; } catch { return false; }
}

/** The query string for a chosen time: zones (the first is where the time is read), local time and working hours. No names or accounts. */
export function shareQuery(s: ShareState): string {
  const p = new URLSearchParams();
  p.set("z", s.zones.slice(0, MAX_SHARE_ZONES).join(","));
  p.set("t", s.time);
  if (s.work.start !== DEFAULT_WORK.start || s.work.end !== DEFAULT_WORK.end) p.set("w", `${s.work.start}-${s.work.end}`);
  return p.toString();
}

/** Reads a share link back. Anything that does not check out (an unknown zone, a bad time) returns null instead of being guessed. */
export function parseShare(search: string): ShareState | null {
  const p = new URLSearchParams(search);
  const z = p.get("z"), t = p.get("t");
  if (!z || !t) return null;
  const zones = z.split(",").map((x) => x.trim()).filter(Boolean);
  if (zones.length < 1 || zones.length > MAX_SHARE_ZONES || !zones.every(zoneOk)) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(t);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
  let work = DEFAULT_WORK;
  const w = p.get("w");
  if (w) {
    const wm = /^(\d{1,2})-(\d{1,2})$/.exec(w);
    if (!wm || Number(wm[1]) >= Number(wm[2]) || Number(wm[2]) > 24) return null;
    work = { start: Number(wm[1]), end: Number(wm[2]) };
  }
  return { zones, time: t, work };
}

// ---------- calendar file ----------

const icsEscape = (s: string) => s.replace(/\\/g, "\\\\").replace(/;/g, "\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");
/** RFC 5545: lines over 75 octets are folded with CRLF and a space. Folds on code point boundaries. */
export function foldIcsLine(line: string): string {
  const enc = new TextEncoder();
  if (enc.encode(line).length <= 75) return line;
  const out: string[] = []; let cur = "", bytes = 0, limit = 75;
  for (const ch of Array.from(line)) {
    const n = enc.encode(ch).length;
    if (bytes + n > limit) { out.push(cur); cur = ""; bytes = 0; limit = 74; }
    cur += ch; bytes += n;
  }
  out.push(cur);
  return out.join("\r\n ");
}
const icsStamp = (ms: number) => new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");

/** A one-event calendar file at an exact instant (UTC, so every calendar shows it in the viewer's own zone), with the times in each chosen zone in the description. */
export function buildIcs(o: { title: string; startUtc: number; minutes: number; zones: string[]; now: number; link?: string }): string {
  const lines = o.zones.map((z) => {
    const p = localParts(o.startUtc, z);
    const f = friendlyZone(o.startUtc, z);
    return `${f.text}: ${p.y}-${pad(p.mo)}-${pad(p.d)} ${pad(p.h)}:${pad(p.mi)}`;
  });
  const desc = [...lines, ...(o.link ? ["", o.link] : []), "", "Time zones planned with FreeDailyPro.com"].join("\n");
  const uid = `${icsStamp(o.startUtc)}-${o.zones.length}-${o.zones[0].replace(/[^A-Za-z0-9]/g, "")}@freedailypro.com`;
  const body = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//FreeDailyPro//Time Zone Converter//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH", "BEGIN:VEVENT",
    `UID:${uid}`, `DTSTAMP:${icsStamp(o.now)}`, `DTSTART:${icsStamp(o.startUtc)}`, `DTEND:${icsStamp(o.startUtc + o.minutes * 60000)}`,
    `SUMMARY:${icsEscape(o.title || "Meeting")}`, `DESCRIPTION:${icsEscape(desc)}`, "END:VEVENT", "END:VCALENDAR",
  ].map(foldIcsLine);
  return body.join("\r\n") + "\r\n";
}
